import React from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { postApi } from '../utils/api';
import { Post, PaginatedResponse } from '../types';
import { formatDistanceToNow } from 'date-fns';
import { zhCN, enUS } from 'date-fns/locale';
import { MessageCircle, Eye, Heart } from 'lucide-react';
import { useLanguageStore } from '../stores/languageStore';

const SearchResults: React.FC = () => {
  const { language } = useLanguageStore();
  const [searchParams] = useSearchParams();
  const keyword = searchParams.get('keyword') || '';

  const [posts, setPosts] = React.useState<Post[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [page, setPage] = React.useState(1);
  const [pagination, setPagination] = React.useState({
    page: 1,
    pageSize: 20,
    total: 0,
    totalPages: 0,
  });

  React.useEffect(() => {
    if (keyword) {
      searchPosts();
    }
  }, [keyword, page]);

  const searchPosts = async () => {
    try {
      setLoading(true);
      const response: PaginatedResponse<Post> = await postApi.searchPosts({
        keyword,
        page,
        pageSize: 20,
      });
      if (response.success) {
        setPosts(response.data || []);
        setPagination(response.pagination);
      }
    } catch (error) {
      console.error('Failed to search posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true, locale: language === 'en' ? enUS : zhCN });
    } catch {
      return dateString;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">
          {language === 'en' ? 'Search Results' : '搜索结果'}
          {keyword && (
            <span className="text-base font-normal text-gray-500 ml-2">
              {language === 'en' ? 'Keyword' : '关键词'}: "{keyword}"
            </span>
          )}
        </h1>
        <span className="text-sm text-gray-500">{language === 'en' ? `${pagination.total} results` : `共 ${pagination.total} 条结果`}</span>
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      ) : posts.length > 0 ? (
        <>
          <div className="space-y-4">
            {posts.map((post) => (
              <article key={post.id} className="card p-4 hover:shadow-md transition-shadow">
                <div className="flex gap-4">
                  <Link to={`/user/${post.author_id}`} className="flex-shrink-0">
                    <img
                      src={post.author_avatar || `https://ui-avatars.com/api/?name=${post.author_name}&background=3b82f6&color=fff`}
                      alt={post.author_name}
                      className="w-12 h-12 rounded-full"
                    />
                  </Link>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <Link
                          to={`/post/${post.id}`}
                          className="text-lg font-semibold text-gray-900 hover:text-primary-600 line-clamp-1"
                        >
                          {post.title}
                        </Link>
                      </div>
                    </div>

                    <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                      <Link
                        to={`/user/${post.author_id}`}
                        className="hover:text-primary-600"
                      >
                        {post.author_name}
                      </Link>
                      <span>Lv.{post.author_level}</span>
                      <span>{formatDate(post.created_at)}</span>
                    </div>

                    <div className="flex items-center gap-4 mt-3 text-sm text-gray-500">
                      <span className="flex items-center">
                        <Eye className="w-4 h-4 mr-1" />
                        {post.views}
                      </span>
                      <span className="flex items-center">
                        <MessageCircle className="w-4 h-4 mr-1" />
                        {post.replies_count}
                      </span>
                      <span className="flex items-center">
                        <Heart className="w-4 h-4 mr-1" />
                        {post.likes}
                      </span>
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </div>

          {/* 分页 */}
          {pagination.totalPages > 1 && (
            <div className="flex justify-center items-center gap-2 mt-6">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="btn btn-secondary disabled:opacity-50"
              >
                {language === 'en' ? 'Previous' : '上一页'}
              </button>
              <span className="text-sm text-gray-600">
                {language === 'en' ? `Page ${page} / ${pagination.totalPages}` : `第 ${page} / ${pagination.totalPages} 页`}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
                disabled={page === pagination.totalPages}
                className="btn btn-secondary disabled:opacity-50"
              >
                {language === 'en' ? 'Next' : '下一页'}
              </button>
            </div>
          )}
        </>
      ) : (
        <div className="text-center py-12">
          <p className="text-gray-500">{language === 'en' ? 'No related posts found' : '没有找到相关帖子'}</p>
        </div>
      )}
    </div>
  );
};

export default SearchResults;
